<template>
  <section>
    <!-- 각 밴드별 id 값이 필요합니다. -->
    <div
      v-if="sectionInfo && isVisible"
      :id="'multisection_index_' + multisectionIndex"
      v-on-display="{ sectionInfo, context: this }"
      :class="multiSectionClass"
      :style="multiSectionColor"
    >
      <!-- title -->
      <div v-if="!isHiddenTitle" class="title">
        <template v-if="isSearchView">
          <h1 v-band-title class="title-area" tabindex="0">
            <span class="label">{{ sectionInfo.title }}</span>
            <span v-if="hasCount" class="count">{{
              sectionInfo.count | setNumberCommas
            }}</span>
          </h1>
        </template>
        <template v-else>
          <h1 v-band-title class="title-area" tabindex="0">
            <span class="label">{{ multiSectionTitle }}</span>
            <span
              v-if="hasCount && (isTagSearchView || isTagGroupSearchView)"
              class="count"
              >{{ sectionInfo.count | setNumberCommas }}</span
            >
          </h1>

          <!-- 다음 밴드의 id 값이 필요합니다. -->
          <a v-if="!isPlayer" :href="nextBandMove" class="a11y-hidden"
            >다음 밴드로 이동</a
          >
        </template>
      </div>
      <!-- title end -->
      <template v-if="isSearchView || isPrerenderer">
        <CellList
          :cell-list="cellList"
          :title="multiSectionTitle"
          :thumbnail-info="thumbnailInfo"
        />
      </template>
      <template v-else>
        <lazy-component @show="setData()">
          <template v-if="cellList.length === 0 && hasEmptyMessage">
            <div class="no-data" tabindex="0">
              <p v-band-title>
                <span>{{ emptyCellMessage }}</span>
              </p>
            </div>
          </template>
          <template v-else>
            <CellList
              :cell-list="cellList"
              :title="multiSectionTitle"
              :thumbnail-info="thumbnailInfo"
            />
          </template>
        </lazy-component>
      </template>
      <template v-if="isSearchView && hasViewMore">
        <a
          href="#"
          class="btn-more all"
          @click.prevent="viewAll(sectionInfo)"
          >{{ viewMoreTitle(sectionInfo, '더보기') }}</a
        >
      </template>
      <template
        v-else-if="(isTagSearchView || isTagGroupSearchView) && hasViewMore"
      >
        <a
          href="#"
          class="btn-more all"
          @click.prevent="viewAll(sectionInfo)"
          >{{ viewMoreTitle(sectionInfo, '전체보기') }}</a
        >
      </template>
      <template v-else-if="isMyView && hasViewMore">
        <a
          href="#"
          class="btn-more all"
          @click.prevent="viewMore(sectionInfo)"
          >{{ viewMoreTitle(sectionInfo, '더보기') }}</a
        >
      </template>
      <template v-else>
        <a
          v-if="hasViewMore"
          href="#"
          class="btn-more all"
          @click.prevent="viewMore(sectionInfo)"
          >{{ viewMoreTitle(sectionInfo, '더보기') }}</a
        >
      </template>
    </div>
    <template v-if="hasButtonInfo">
      <ppv-wrapper :button-info="buttonInfo"></ppv-wrapper>
    </template>
  </section>
</template>

<script>
  import { bandTitle, onDisplay } from '@/common/resources/directives'
  import { fetchOnContent } from '@/common/utils/request.common'
  import { getButtonInfo, getCellList } from '@/common/utils/gnb.fp'
  import MultiSectionMixin from '@/mixins/multisection.mixin'
  import CellList from '@/components/band/CellList'
  import { isPrerenderer, setNumberCommas } from '@/common/utils/common'
  import _ from 'lodash'
  import {
    MY_BAND_DEFAULT_EMPTY_MESSAGE,
    MY_BAND_EMPTY_MESSAGE,
  } from '@/common/resources/const'
  import PpvWrapper from '@/components/band/wrapper/PpvWrapper'
  export default {
    name: 'TemplateMultiSection',
    filters: {
      setNumberCommas,
    },
    directives: {
      bandTitle,
      onDisplay,
    },
    components: {
      CellList,
      PpvWrapper,
    },
    mixins: [MultiSectionMixin],
    props: {
      sectionInfo: {
        type: Object,
      },
      thumbnailInfo: {
        type: Object,
      },
      multisectionIndex: {
        type: Number,
      },
    },
    data() {
      return {
        buttonInfo: null,
        cellList: [],
        isVisible: true,
        titleHidden: false,
        hasEmptyMessage: false,
        isPrerenderer: isPrerenderer(),
        multisectionId: '',
        isPlayer: false,
      }
    },
    computed: {
      isSearchView() {
        return this.$route.name === 'search'
      },
      isTagSearchView() {
        return this.$route.name === 'search_tag'
      },
      isTagGroupSearchView() {
        return this.$route.name === 'search_tag_group'
      },
      isMyView() {
        return this.$route.name === 'my'
      },
      hasButtonInfo() {
        return this.buttonInfo !== null
      },
      isHiddenTitle() {
        return _.get(this.sectionInfo, 'cell_type', '') === 'band_61'
      },
      emptyCellMessage() {
        const emptyMessageList = MY_BAND_EMPTY_MESSAGE.filter(
          (emptyMessage) => {
            const title = this.multiSectionTitle.replace(
              /(@\d@)|(@\^\d@)/gi,
              '',
            )
            return emptyMessage.title.trim() === title.trim()
          },
        )
        if (emptyMessageList.length > 0) {
          return _.get(
            emptyMessageList,
            '0.emptyMessage',
            MY_BAND_DEFAULT_EMPTY_MESSAGE,
          )
        } else {
          return MY_BAND_DEFAULT_EMPTY_MESSAGE
        }
      },
      multiSectionClass() {
        const type = _.get(this.thumbnailInfo, 'type', '')
        return type === 'band_94' || type === 'band_98'
          ? 'multi portrait-cell ranking-band'
          : `multi ${_.get(this.thumbnailInfo, 'componentName', '')}`
      },
      nextBandMove() {
        return `#multisection_index_${this.multisectionIndex + 1}`
      },
    },
    created() {
      this.isPlayer = _.get(this.$route, 'name', '').indexOf('player') > -1
      if (this.isSearchView || this.isPrerenderer) {
        this.setData()
      }
    },
    methods: {
      setVisibleState() {
        const emptyCellList = this.cellList.length === 0
        if (emptyCellList) {
          if (this.isMyView) {
            this.isVisible = true
            this.hasEmptyMessage = true
          } else {
            this.isVisible = false
            this.hasEmptyMessage = false
          }
        } else {
          this.isVisible = true
        }
      },
      async setData() {
        if (this.cellList.length !== 0) return
        try {
          const response = await fetchOnContent(this.sectionInfo)
          this.cellList = getCellList(response)
          /** TODO: 검색 화면에서 보여질때에는
           *  현재 검색어, 카테고리, 각 밴드 cellList 의 첫 3 개의 상품정보를 추출해서 보낸다.
           **/
          this.buttonInfo = getButtonInfo(response)
          this.setVisibleState()
        } catch (e) {
          this.isVisible = false
        }
      },
      changColorSearchWord(title) {
        const searchWord = _.get(this.$route, 'query.searchWord', '')
        return _.replace(
          title,
          new RegExp(searchWord, 'g'),
          `<span class="col-vari-blue">${searchWord}</span>`,
        )
      },
    },
  }
</script>
<style lang="scss" src="@/assets/scss/category/new_band.scss"></style>
<style lang="css" scoped>
  /*.multi-chTemplate-comm.multi {width:100%; margin:0 auto; padding:50px 0; background-color: #faf8ff;}*/
  /*.multi-chTemplat-comm.multi > div {width:1240px; margin:0 auto;}*/

  .multi.landscape-cell {
    min-height: 238px;
  }
  .multi.circle-cell {
    min-height: 202px;
  }
  .multi.portrait-cell {
    min-height: 383px;
  }
  .multi.editor-pick-package-cell {
    min-height: 366px;
  }
  .multi.autoplay-band {
    min-height: 421px;
  }
</style>
